/**
 * Тонкая обёртка над window.Telegram.WebApp.
 *
 * Зачем обёртка, а не прямые вызовы по коду: в Phase 2 приложение переезжает в
 * Capacitor / React Native, где никакого window.Telegram нет. Тогда заменяется
 * только этот файл (и появляется вторая реализация того же интерфейса), а
 * компоненты остаются как есть.
 *
 * Второй мотив — методы Mini App версионируются. Часть из них падает на старых
 * клиентах, поэтому каждый вызов защищён проверкой доступности.
 */

export type InvoiceStatus = 'paid' | 'cancelled' | 'failed' | 'pending';

interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  section_bg_color?: string;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: { id: number; first_name?: string; last_name?: string; username?: string; photo_url?: string };
    start_param?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: TelegramThemeParams;
  isExpanded: boolean;
  viewportStableHeight: number;

  ready(): void;
  expand(): void;
  close(): void;
  isVersionAtLeast(version: string): boolean;

  openInvoice(url: string, callback?: (status: InvoiceStatus) => void): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  openTelegramLink?(url: string): void;

  enableClosingConfirmation?(): void;
  disableVerticalSwipes?(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;

  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };

  BackButton?: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  MainButton?: {
    setParams(params: { text?: string; is_visible?: boolean; is_active?: boolean }): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

export function getWebApp(): TelegramWebApp | undefined {
  return window.Telegram?.WebApp;
}

export function isInsideTelegram(): boolean {
  // initData пустая, если страницу открыли в обычном браузере: сам объект
  // WebApp скрипт создаёт всегда, поэтому проверять надо именно данные.
  return Boolean(getWebApp()?.initData);
}

/**
 * Готовность приложения. Вызывать один раз при старте:
 * до `ready()` Telegram держит скелетон загрузки поверх вебвью.
 */
export function initTelegram(): void {
  const webApp = getWebApp();
  if (!webApp) return;

  webApp.ready();
  webApp.expand();

  // Вертикальный свайп по вебвью сворачивает Mini App. У нас горизонтальный
  // пейджер и вертикальные списки — коллапс по случайному жесту раздражает.
  // Метод появился в 7.7, на старых клиентах его просто нет.
  if (webApp.isVersionAtLeast('7.7')) {
    webApp.disableVerticalSwipes?.();
  }

  if (webApp.isVersionAtLeast('6.1')) {
    webApp.setBackgroundColor?.('#141414');
  }

  if (webApp.isVersionAtLeast('6.9')) {
    webApp.setHeaderColor?.('#141414');
  }

  applyThemeVariables(webApp);
}

/**
 * Переносит themeParams в CSS-переменные, чтобы компоненты не знали про
 * Telegram и просто читали `var(--tg-theme-bg-color)`.
 */
const JET_THEME: Record<string, string> = {
  'bg-color': '#141414',
  'secondary-bg-color': '#1c1c1e',
  'text-color': '#f4f4f7',
  'hint-color': '#9a9aa8',
  'link-color': '#8b6bff',
  'button-color': '#7b6cff',
  'button-text-color': '#ffffff',
  'header-bg-color': '#141414',
  'section-bg-color': '#1c1c1e',
};

function applyThemeVariables(webApp: TelegramWebApp): void {
  const root = document.documentElement;

  for (const [key, value] of Object.entries(webApp.themeParams)) {
    if (value) {
      root.style.setProperty(`--tg-theme-${key.replace(/_/g, '-')}`, value);
    }
  }

  for (const [key, value] of Object.entries(JET_THEME)) {
    root.style.setProperty(`--tg-theme-${key}`, value);
  }

  root.dataset.colorScheme = 'dark';
}

/** Строка для обмена на JWT. В dev-режиме берётся из .env. */
export function getInitData(): string {
  // Сначала hash текущего запуска: на iOS telegram-web-app.js иногда
  // подставляет в WebApp.initData прошлую сессию с этого же домена —
  // тогда HMAC считается токеном чужого бота и вход отклоняется.
  const fromLaunchUrl = readInitDataFromLocation();
  if (fromLaunchUrl) return fromLaunchUrl;

  const real = getWebApp()?.initData;
  if (real) return real;

  if (import.meta.env.DEV && import.meta.env.VITE_DEV_INIT_DATA) {
    return import.meta.env.VITE_DEV_INIT_DATA;
  }

  return '';
}

function readInitDataFromLocation(): string {
  if (typeof window === 'undefined') return '';

  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  if (hash) {
    const fromHash = new URLSearchParams(hash).get('tgWebAppData');
    if (fromHash) return fromHash;
  }

  const search = window.location.search.startsWith('?') ? window.location.search.slice(1) : window.location.search;
  if (search) {
    const fromQuery = new URLSearchParams(search).get('tgWebAppData');
    if (fromQuery) return fromQuery;
  }

  return '';
}

/**
 * Оплата через Telegram Stars.
 *
 * `openInvoice` — колбэчный API, оборачиваем в промис. Важно: 'paid' означает
 * лишь то, что клиент увидел успех. Источник правды — наш бэкенд, который
 * получил successful_payment, поэтому после 'paid' список покупок нужно
 * перезапросить, а не рисовать товар оптимистично.
 */
export function openInvoice(url: string): Promise<InvoiceStatus> {
  const webApp = getWebApp();

  if (!webApp) {
    // Вне Telegram: открываем ссылку в новой вкладке, статус узнать нельзя.
    window.open(url, '_blank', 'noopener');
    return Promise.resolve('pending');
  }

  return new Promise((resolve) => {
    let settled = false;

    webApp.openInvoice(url, (status) => {
      if (settled) return;
      settled = true;
      resolve(status);
    });

    // Подстраховка: на части клиентов колбэк не приходит, если пользователь
    // закрыл окно оплаты жестом. Без таймаута промис висел бы навсегда.
    setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve('pending');
      }
    }, 5 * 60 * 1000);
  });
}

export function haptic(type: 'success' | 'error' | 'warning'): void {
  getWebApp()?.HapticFeedback?.notificationOccurred(type);
}

export function hapticSelection(): void {
  getWebApp()?.HapticFeedback?.selectionChanged();
}

export type HapticImpact = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';

/** Короткий удар, как щелчок переключателя на iPhone. rigid/soft есть с WebApp 7.7. */
export function hapticImpact(style: HapticImpact): void {
  const webApp = getWebApp();
  const feedback = webApp?.HapticFeedback;
  if (!feedback) return;

  const resolved =
    (style === 'rigid' || style === 'soft') && webApp && !webApp.isVersionAtLeast('7.7') ? 'medium' : style;

  feedback.impactOccurred(resolved);
}

/** Два импульса подряд: так на iPhone ощущается подтверждение оплаты. */
export function hapticPaySuccess(): void {
  hapticImpact('heavy');
  window.setTimeout(() => haptic('success'), 90);
}

/** Кнопка «назад» Telegram. Снимаем слушатель при размонтировании. */
export function bindBackButton(onBack: () => void): () => void {
  const button = getWebApp()?.BackButton;
  if (!button) return () => undefined;

  button.onClick(onBack);
  button.show();

  return () => {
    button.offClick(onBack);
  };
}

export function hideBackButton(): void {
  getWebApp()?.BackButton?.hide();
}

/** Чаты и боты лучше открывать через openTelegramLink — иначе iOS уводит в Safari. */
export function openTelegram(url: string): void {
  const webApp = getWebApp();

  if (webApp?.openTelegramLink) {
    webApp.openTelegramLink(url);
    return;
  }

  if (webApp) {
    webApp.openLink(url);
    return;
  }

  window.open(url, '_blank', 'noopener');
}

/**
 * Tribute отдаёт t.me/tribute/app?startapp=… — это Telegram-ссылка.
 * Браузерный paymentUrl открываем через openLink, чтобы не выпасть в Safari зря.
 */
export function openCheckoutLink(url: string): void {
  if (/^https?:\/\/t\.me\//i.test(url)) {
    openTelegram(url);
    return;
  }

  const webApp = getWebApp();

  if (webApp) {
    webApp.openLink(url);
    return;
  }

  window.open(url, '_blank', 'noopener');
}
